import { cn } from '@commenda/utils';
import {
  Line,
  LineChart as RechartsLineChart,
  ResponsiveContainer,
  Tooltip,
  YAxis,
  type DotProps,
} from 'recharts';

import { ChartTooltip } from './ChartTooltip';
import { ChartHelper, DEFAULT_VALUE_FORMATTER } from './chartHelpers';
import type { ChartValueFormatter } from './chartTypes';

export interface ISparklinePoint {
  label: string;
  value: number | null;
}

export interface ISparklineProps {
  data: ISparklinePoint[];
  valueFormatter?: ChartValueFormatter;
  /** Compact values drawn above the first and last points. */
  startLabel?: string;
  endLabel?: string;
  /** Month captions drawn under the first and last points. */
  startCaption?: string;
  endCaption?: string;
  className?: string;
}

const SERIES = 'value';
const ENDPOINT_RADIUS = 3;

/**
 * Single-series trend line with no axes or gridlines. Only the first and last
 * points are marked, so the eye lands on where the series started and where it is now.
 */
export const Sparkline = ({
  data,
  valueFormatter = DEFAULT_VALUE_FORMATTER,
  startLabel,
  endLabel,
  startCaption,
  endCaption,
  className,
}: ISparklineProps) => {
  const present = data.filter((d) => d.value !== null);
  const endpoints = new Set([
    present[0]?.label,
    present[present.length - 1]?.label,
  ]);

  const renderDot = (props: DotProps & { payload?: ISparklinePoint }) => {
    const { cx, cy, payload } = props;
    if (!payload || !endpoints.has(payload.label) || cx == null || cy == null)
      return <g key={payload?.label} />;
    return (
      <circle
        key={payload.label}
        cx={cx}
        cy={cy}
        r={ENDPOINT_RADIUS}
        className="fill-fg-secondary"
      />
    );
  };

  return (
    <div className={cn('flex w-full flex-col gap-xs', className)}>
      <div className="flex items-end justify-between txt-extra-small-medium tabular-nums text-text-secondary">
        <span>{startLabel}</span>
        <span>{endLabel}</span>
      </div>
      <div className="h-12 w-full">
        <ResponsiveContainer width="100%" height="100%">
          <RechartsLineChart
            data={data}
            margin={{ top: 4, right: 4, bottom: 4, left: 4 }}
          >
            <YAxis hide domain={['auto', 'auto']} />
            <Tooltip
              wrapperStyle={{ outline: 'none' }}
              isAnimationActive={false}
              cursor={false}
              content={({ active, payload, label }) => (
                <ChartTooltip
                  active={active}
                  label={ChartHelper.tooltipLabel(label)}
                  payload={ChartHelper.tooltipEntries(
                    payload,
                    new Map([[SERIES, 'gray']])
                  )}
                  valueFormatter={valueFormatter}
                />
              )}
            />
            <Line
              dataKey={SERIES}
              type="monotone"
              className="stroke-fg-secondary"
              stroke=""
              strokeWidth={1.5}
              connectNulls={false}
              isAnimationActive={false}
              dot={renderDot}
              activeDot={{
                r: ENDPOINT_RADIUS,
                strokeWidth: 0,
                className: 'fill-fg-secondary',
              }}
            />
          </RechartsLineChart>
        </ResponsiveContainer>
      </div>
      <div className="flex justify-between txt-extra-small-regular text-text-tertiary">
        <span>{startCaption}</span>
        <span>{endCaption}</span>
      </div>
    </div>
  );
};
